import { cn } from '@/lib/utils';

interface CircularProgressProps {
  value: number;
  max: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Circular SVG progress indicator.
 * Shows a ring that fills clockwise as value approaches max.
 * Turns green when complete.
 */
export function CircularProgress({
  value,
  max,
  size = 32,
  strokeWidth = 3,
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = max > 0 ? value / max : 0;
  const offset = circumference * (1 - percentage);
  const isComplete = value === max && max > 0;

  return (
    <div className="flex items-center gap-2">
      <svg width={size} height={size} className="-rotate-90">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted"
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(
            'transition-all duration-500',
            isComplete ? 'text-green-500' : 'text-primary',
          )}
        />
      </svg>
      <span className="text-muted-foreground text-xs tabular-nums">
        {value}/{max}
      </span>
    </div>
  );
}
